// ── Festive banner content ───────────────────────────────────────────────────
// This file is the ONLY thing that changes each year. Festival content (dates,
// colours, artwork, greeting, thought) ships with the frontend release so every
// firm gets the same polished banner and nobody can post off-brand text. Firms
// only choose which festivals to show, their sign-off line and an optional note
// (Settings → Firm & Branding → Festive Banner).
//
// Fixed-date days use `recur: ['MM-DD','MM-DD']` and repeat every year on their
// own. Lunar festivals use `from` / `to` (YYYY-MM-DD) and need new dates each
// year — add next year's entry before the festival; an entry whose dates have
// passed simply stops showing.
//
// `art` is a key of FESTIVE_ART (festiveArt.js). `counterOf: N` shows "Day n of N";
// `badge` shows fixed text. A firm's own note replaces either.

export const FESTIVALS = [
  { id: 'navratri', name: 'Navratri', from: '2026-10-11', to: '2026-10-19',
    f1: '#b91c1c', f2: '#e8a317', art: 'navratri', counterOf: 9,
    title: 'Shubh Navratri',
    sub: 'May Maa Durga bless you and your family with strength, wisdom and peace through these nine sacred days.',
    thought: 'Shakti lives in every honest effort.' },
  { id: 'dussehra', name: 'Dussehra', from: '2026-10-20', to: '2026-10-20',
    f1: '#c2410c', f2: '#d97706', art: 'dussehra', badge: 'Vijayadashami',
    title: 'Happy Dussehra',
    sub: 'May truth guide every decision you make, and may every hurdle fall before your patience and courage. Wishing you and your family victory, health and happiness.',
    thought: 'Every Ravan falls to truth, patience and courage.' },
  { id: 'diwali', name: 'Diwali', from: '2026-11-05', to: '2026-11-10',
    f1: '#b45309', f2: '#d97706', art: 'diwali',
    title: 'Shubh Deepavali',
    sub: 'May the light of Diwali bring prosperity to your home, peace to your heart and success to everything you begin.',
    thought: 'A single diya can light a thousand more. Share your light.' },
  { id: 'chhath', name: 'Chhath Puja', from: '2026-11-13', to: '2026-11-16',
    f1: '#ea580c', f2: '#ca8a04', art: 'chhath',
    title: 'Happy Chhath Puja',
    sub: 'May Surya Dev and Chhathi Maiya bless you and your family with good health, happiness and a long life.',
    thought: 'Gratitude, like the sunrise, makes every day begin well.' },
  { id: 'holi', name: 'Holi', from: '2027-03-20', to: '2027-03-23',
    f1: '#db2777', f2: '#0d9488', art: 'holi',
    title: 'Happy Holi',
    sub: 'May the colours of Holi bring joy to your home, let the past go and bring people closer together.',
    thought: 'Colours are brighter when they are shared.' },
  { id: 'republic', name: 'Republic Day', recur: ['01-24', '01-26'],
    f1: '#c2570c', f2: '#138808', art: 'chakra', tri: true, badge: '26 January',
    title: 'Happy Republic Day',
    sub: 'Today we honour the Constitution that gives every citizen equal rights and dignity. Wishing you a proud Republic Day.',
    thought: 'A strong nation is built by people who do their duty well.' },
  { id: 'independence', name: 'Independence Day', recur: ['08-13', '08-15'],
    f1: '#c2570c', f2: '#138808', art: 'chakra', tri: true, badge: '15 August',
    title: 'Happy Independence Day',
    sub: 'Freedom came through sacrifice and is kept alive by honesty and hard work. Wishing you a proud Independence Day. Jai Hind.',
    thought: 'Freedom is a responsibility we share every day.' },
];

export const isoOf = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// [from, to] ISO strings for a festival in a given year.
export const festivalRange = (f, year) =>
  f.recur ? [`${year}-${f.recur[0]}`, `${year}-${f.recur[1]}`] : [f.from, f.to];

export const formatShortDate = iso =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

// The festival running on `iso`, honouring the firm's switches. Returns null when
// nothing is on, the banner is switched off, or this festival is switched off.
export function activeFestival(iso, settings = {}) {
  if (settings.enabled === false) return null;
  const off = settings.off || [];
  const year = iso.slice(0, 4);
  return FESTIVALS.find(f => {
    if (off.includes(f.id)) return false;
    const [from, to] = festivalRange(f, year);
    return iso >= from && iso <= to;
  }) || null;
}

// Small pill next to the festival name: the firm's own note wins, then "Day n of N", then fixed text.
export function festivalTag(f, iso, note) {
  if (note) return note;
  if (f.counterOf) {
    const [from] = festivalRange(f, iso.slice(0, 4));
    const day = Math.round((Date.parse(iso) - Date.parse(from)) / 86400000) + 1;
    return `Day ${day} of ${f.counterOf}`;
  }
  return f.badge || '';
}

// A date inside the festival, for previews on the settings page.
export const previewDate = f => festivalRange(f, f.recur ? String(new Date().getFullYear()) : f.from.slice(0, 4))[0];
